/**
 *
 * DeletionConfirmDialog
 *
 */

import { FastField, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  CircularProgress,
  PaperProps,
  Paper,
} from '@mui/material';
import { FormTextControl } from '@assetic/web-components';
import { makeThemeStyle } from '../../../styles/makeThemeStyle';
import React, { ReactNode, useRef, useEffect, memo } from 'react';
import { TextWithCopyAction } from '../TextWithCopyAction';
import { format } from 'react-string-format';
import { useButtonStyles } from '../../../styles/buttonStyles';
import { DialogWrapper } from '../DialogWrapper';
import { DialogTitleWrapper } from '../DialogTitleWrapper';
import Draggable from 'react-draggable';

const useStyles = makeThemeStyle(theme => ({
  text: {
    fontSize: '14px',
    color: '#333f48',
    marginBottom: theme.spacing(3),
  },
  footer: {
    justifyContent: 'space-between',
    padding: theme.spacing(0, 3, 3),
  },
  circularWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  circular: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

interface DeletionConfirmDialogProps {
  onAccept: () => void;
  onClose: () => void;
  open: boolean;
  title: ReactNode;
  message: ReactNode;
  deleteWarningText?: ReactNode;
  warningMessage?: ReactNode;
  confirmActionOverride?: string;
  submitButtonTextOverride?: string;
  cancelButtonTextOverride?: string;
  entityName?: ReactNode;
  confirmValue?: string;
  processing?: boolean;
  requireValidate?: boolean;
  showSubmitButton?: boolean;
}

function PaperComponent(props: PaperProps) {
  return (
    <Draggable cancel={'.c-deleteConfirmDialog__content'}>
      <Paper {...props} />
    </Draggable>
  );
}

export const DeletionConfirmDialog = (props: DeletionConfirmDialogProps) => {
  const {
    open,
    onAccept,
    onClose,
    title,
    message,
    deleteWarningText,
    warningMessage,
    confirmActionOverride,
    submitButtonTextOverride,
    cancelButtonTextOverride,
    entityName,
    confirmValue,
    processing,
    requireValidate = true,
    showSubmitButton = true,
  } = props;

  const formRef = useRef<FormikProps<{ name: string }> | null>(null);
  const styles = useStyles();
  const buttonStyles = useButtonStyles();
  useEffect(() => {
    !!formRef.current && formRef.current.resetForm();
  }, [open]);

  const handleClose = () => {
    onClose();
  };
  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        name: '',
      }}
      validationSchema={
        requireValidate
          ? Yup.object().shape({
              name: Yup.string()
                .required(`Input '${confirmValue}' is required`)
                .oneOf(
                  [confirmValue],
                  `Input does not match with '${confirmValue}'`,
                ),
            })
          : null
      }
      enableReinitialize
      validateOnBlur={true}
      validateOnChange={false}
      onSubmit={onAccept}
    >
      {formProps => (
        <DialogWrapper
          open={open}
          onClose={handleClose}
          paperComponent={PaperComponent}
        >
          <form onSubmit={formProps.handleSubmit} noValidate>
            <DialogTitleWrapper
              id="delete-confirm-form-dialog-title"
              title={title}
              onClose={handleClose}
            />
            <DialogContent className="c-deleteConfirmDialog__content">
              <DialogContentText className={styles.text} component="div">
                <Typography variant="body1">{message}</Typography>
                {warningMessage && (
                  <Typography variant="body1" sx={{ padding: '0.5rem 0 0 0' }}>
                    <b>{deleteWarningText}:</b> {warningMessage}
                  </Typography>
                )}

                {requireValidate ? (
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{ display: 'inline-flex', padding: '0.5rem 0 0 0' }}
                  >
                    {format(
                      'Enter {0} below to confirm {1}.',
                      <TextWithCopyAction
                        text={confirmValue ?? ''}
                        textStyle={{
                          fontStyle: 'italic',
                          fontWeight: 'bold !important',
                        }}
                      />,
                      <>{confirmActionOverride ?? 'deletion'}</>,
                    )}
                  </Typography>
                ) : null}
              </DialogContentText>

              {requireValidate ? (
                <FastField
                  name="name"
                  label={entityName}
                  component={FormTextControl}
                  required
                />
              ) : null}
            </DialogContent>
            <DialogActions
              classes={{
                root: styles.footer,
              }}
            >
              <Button
                className={buttonStyles.normalButton}
                aria-label="Cancel"
                onClick={handleClose}
              >
                {cancelButtonTextOverride ?? 'Cancel'}
              </Button>
              {showSubmitButton && (
                <div className={styles.circularWrapper}>
                  <Button
                    className={buttonStyles.warningButton}
                    type="submit"
                    disabled={processing}
                    data-testid="deletion_confirm_dialog_delBtn"
                  >
                    {submitButtonTextOverride ?? 'Delete'}
                  </Button>
                  {(processing ?? false) && (
                    <CircularProgress size={24} className={styles.circular} />
                  )}
                </div>
              )}
            </DialogActions>
          </form>
        </DialogWrapper>
      )}
    </Formik>
  );
};

export default memo(DeletionConfirmDialog);
